import React, { useEffect } from 'react';
import Navigator from './components/general/Navigator';
import { GlobalStateProvider } from './components/general/GlobalStateProvider';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { Analytics } from '@vercel/analytics/react';
import { DEFAULT_TITLE, DEFAULT_DESCRIPTION, DEFAULT_KEYWORDS, DEFAULT_OG_IMAGE, SITE_URL, SITE_NAME } from './components/general/useSEO';
import { initTelemetry } from './utility/telemetry';

const STRIPE_PUBLISHABLE_KEY = 'pk_live_51SO6P2IzcWAhrGlwYZRNZ3tlU9jvYRbohExXPrxnofjogwSHLN3Lz5gAcWw3XjxWD7m90neFCh1riQIQoUw9IzYx00iyqCsjh0';
// Set immediately so child modules/components can read it during first render.
globalThis.STRIPE_PUBLISHABLE_KEY = STRIPE_PUBLISHABLE_KEY;

// Organization + WebSite JSON-LD injected once at app start — not route-specific
const GLOBAL_JSONLD = [
    {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/favicon-32.png`,
        },
        description: DEFAULT_DESCRIPTION,
        contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            url: `${SITE_URL}/contact-us`,
        },
    },
    {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: SITE_URL,
        name: SITE_NAME,
        description: 'Kids summer camp marketplace — find, book, and manage summer camps',
        publisher: { '@id': `${SITE_URL}/#organization` },
        potentialAction: {
            '@type': 'SearchAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${SITE_URL}/summer-camps/{search_term_string}`,
            },
            'query-input': 'required name=search_term_string',
        },
    },
];

function injectGlobalSEO() {
    if (typeof document === 'undefined') return;

    document.title = DEFAULT_TITLE;

    const upsert = (selector, attrs, contentAttr, content) => {
        let el = document.head.querySelector(selector);
        if (!el) {
            el = document.createElement('meta');
            Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
            document.head.appendChild(el);
        }
        el.setAttribute(contentAttr, content);
    };

    upsert('meta[name="description"]', { name: 'description' }, 'content', DEFAULT_DESCRIPTION);
    upsert('meta[name="keywords"]', { name: 'keywords' }, 'content', DEFAULT_KEYWORDS);
    upsert('meta[property="og:title"]', { property: 'og:title' }, 'content', DEFAULT_TITLE);
    upsert('meta[property="og:description"]', { property: 'og:description' }, 'content', DEFAULT_DESCRIPTION);
    upsert('meta[property="og:image"]', { property: 'og:image' }, 'content', DEFAULT_OG_IMAGE);
    upsert('meta[property="og:type"]', { property: 'og:type' }, 'content', 'website');
    upsert('meta[property="og:url"]', { property: 'og:url' }, 'content', SITE_URL);
    upsert('meta[property="og:site_name"]', { property: 'og:site_name' }, 'content', SITE_NAME);
    upsert('meta[name="twitter:card"]', { name: 'twitter:card' }, 'content', 'summary_large_image');
    upsert('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', DEFAULT_TITLE);
    upsert('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', DEFAULT_DESCRIPTION);
    upsert('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', DEFAULT_OG_IMAGE);
    upsert('meta[name="twitter:site"]', { name: 'twitter:site' }, 'content', '@SnapCampApp');

    // Canonical for root
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
    }
    canonical.href = SITE_URL;

    // Inject global JSON-LD schemas (Organization + WebSite) once
    if (!document.head.querySelector('script[data-seo="global"]')) {
        GLOBAL_JSONLD.forEach(schema => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-seo', 'global');
            script.textContent = JSON.stringify(schema);
            document.head.appendChild(script);
        });
    }
}

export default function App() {
    const shouldEnableVercelAnalytics =
        typeof window !== 'undefined'
        && (
            window.location.hostname.endsWith('vercel.app')
            || window.location.hostname.endsWith('snapcamp.ai')
        );

    useEffect(() => {
        injectGlobalSEO();
        initTelemetry();
    }, []);

    // Initialize Google Analytics
    useEffect(() => {
        // Initialize dataLayer
        window.dataLayer = window.dataLayer || [];
        function gtag(...args) {
            window.dataLayer.push(args);
        }
        window.gtag = gtag;
        gtag('js', new Date());
        gtag('config', 'G-RFE6NMC5Z2');

        // Load Google Analytics script
        const script1 = document.createElement('script');
        script1.async = true;
        script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-RFE6NMC5Z2';
        document.head.appendChild(script1);
    }, []);

    // Initialize the Facebook SDK
    useEffect(() => {
        const loadFacebookSDK = () => {
            if (!window.FB) {
                const script = document.createElement('script');
                script.src = 'https://connect.facebook.net/en_US/sdk.js';
                script.async = true;
                script.defer = true;
                script.onload = () => {
                    window.FB.init({
                        appId: '1197220841295709', // SnapCamp Facebook App ID
                        cookie: true,
                        xfbml: true,
                        version: 'v15.0',
                    });
                };
                document.body.appendChild(script);
            }
        };
        loadFacebookSDK();
    }, []);

    return (
        <I18nextProvider i18n={i18n}>
            <GoogleOAuthProvider clientId="840220596106-h64nrep3a5v8g00adbvnki504sfdqnhf.apps.googleusercontent.com">
                <GlobalStateProvider>
                    <Router>
                        <Navigator />
                    </Router>
                </GlobalStateProvider>
            </GoogleOAuthProvider>
            {shouldEnableVercelAnalytics ? <Analytics /> : null}
        </I18nextProvider>
    );
}

